
const { PRIMITIVE_CONTEXT_TYPES } = require("../helpers/required-context-entity-type");
const { isExpressionGlobalName, reservedGlobalNameMessage } = require("../helpers/expression-globals");

const KEY_URI = "uri";
const KEY_CONSTANT = "constant";
const KEY_CONFIG = "config";
const KEY_CONTEXT = "context";
const KEY_STANDARD = "standard";
const KEY_STANDARD_ROOT = "standardRoot";

const KEY_MAY_BE_EMPTY = "mayBeEmpty";

const FLAT_STANDARD_KINDS = ["currentUser", "configuration", "appUser"];

const KEY_ENGINE_CONFIGURATION = "configuration";
const KEY_REMOTE_LINKING_FIELD = "remoteLinkingField";

const LEGACY_KEY_FEED_SEPARATOR_LABEL = "feedSeparatorLabel";

const VALID_CONTEXT_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

const KEY_STANDARD_RESUME_STORY = "resumeStory";
const KEY_STANDARD_CURRENT_USER_PROFILE = "currentUserProfile";
const KEY_STANDARD_CURRENT_SUBSCRIPTION = "currentSubscription";
const KEY_STANDARD_CURRENT_USER_ACCESS = "currentUserAccess";
const KEY_STANDARD_CONTINUE_ACTIVITY = "continueActivity";
const KEY_STANDARD_CONTINUE_PROGRAM = "continueProgram";
const VALID_STANDARD_ENTITIES = [
  "currentUser",
  "appConfiguration",
  "appUser",
  KEY_STANDARD_RESUME_STORY,
  KEY_STANDARD_CURRENT_USER_PROFILE,
  KEY_STANDARD_CURRENT_SUBSCRIPTION,
  KEY_STANDARD_CURRENT_USER_ACCESS,
  KEY_STANDARD_CONTINUE_ACTIVITY,
  KEY_STANDARD_CONTINUE_PROGRAM,
];

class ProvidesContextEntry {

  static normalize(entry) {
    if (ProvidesContextEntry.isPrimitive(entry)) {
      return ProvidesContextEntry.#normalizePrimitive(entry);
    }
    const normalized = {
      entityType: entry.entityType,
      entitySource: entry.entitySource,
      appName: entry.appName,
      mayBeEmpty: Boolean(entry[KEY_MAY_BE_EMPTY]),
      isLegacyShape: false,
    };
    if (entry.entitySource === KEY_URI) {
      normalized.pkUrlVars = entry.entityPkUrlVars;
    } else if (entry.entitySource === KEY_CONSTANT) {
      if (entry.entityPkVars) {
        normalized.pkVars = entry.entityPkVars;
      } else {
        normalized.pkVars = entry.entityPkUrlVars;
        normalized.isLegacyShape = true;
      }
    } else if (entry.entitySource === KEY_CONFIG) {
      normalized.configurationField = Object.values(entry.entityPkUrlVars ?? {})[0];
      normalized.isLegacyShape = true;
    } else if (entry.entitySource === KEY_ENGINE_CONFIGURATION && entry[KEY_REMOTE_LINKING_FIELD]) {
      normalized.entitySource = KEY_CONFIG;
      normalized.configurationField = entry[KEY_REMOTE_LINKING_FIELD];
    } else if (entry.entitySource === KEY_CONTEXT) {
      normalized.sourceContext = entry.sourceContext;
      normalized.sourceField = entry.sourceField;
    } else if (entry.entitySource === KEY_STANDARD) {
      normalized.standardEntity = entry.standardEntity;
    } else if (entry.entitySource === KEY_STANDARD_ROOT) {
      normalized.rootName = entry.rootName;
    } else if (FLAT_STANDARD_KINDS.includes(entry.entitySource)) {
      normalized.entitySource = KEY_STANDARD;
      normalized.standardEntity = entry.entitySource === "configuration"
        ? "appConfiguration"
        : entry.entitySource;
    }
    return normalized;
  }

  static build(formState) {
    if (ProvidesContextEntry.isPrimitive(formState)) {
      return ProvidesContextEntry.#buildPrimitive(formState);
    }
    const built = {
      entityType: formState.entityType,
      entitySource: formState.entitySource,
    };
    if (formState.entitySource === KEY_URI) {
      built.entityPkUrlVars = formState.pkUrlVars;
    } else if (formState.entitySource === KEY_CONSTANT) {
      built.entityPkVars = formState.pkVars;
    } else if (formState.entitySource === KEY_CONFIG) {
      built.entityPkUrlVars = formState.pkUrlVars;
    } else if (formState.entitySource === KEY_CONTEXT) {
      built.sourceContext = formState.sourceContext;
      if (formState.sourceField) {
        built.sourceField = formState.sourceField;
      }
    } else if (formState.entitySource === KEY_STANDARD) {
      built.standardEntity = formState.standardEntity;
    } else if (formState.entitySource === KEY_STANDARD_ROOT) {
      built.rootName = formState.rootName;
    }
    if (formState.appName) {
      built.appName = formState.appName;
    }
    if (formState[KEY_MAY_BE_EMPTY]) {
      built[KEY_MAY_BE_EMPTY] = true;
    }
    return built;
  }

  static referencedSourceContexts(entriesMap) {
    const referenced = {};
    for (const [name, entry] of Object.entries(entriesMap ?? {})) {
      if (entry?.entitySource !== KEY_CONTEXT || !entry.sourceContext) {
        continue;
      }
      if (!referenced[entry.sourceContext]) {
        referenced[entry.sourceContext] = [];
      }
      referenced[entry.sourceContext].push(name);
    }
    return referenced;
  }

  static isValueSource(entry) {
    return entry?.entitySource === LEGACY_KEY_FEED_SEPARATOR_LABEL;
  }

  static isPrimitive(entry) {
    return Boolean(entry)
      && typeof entry === "object"
      && !Array.isArray(entry)
      && !entry.entityType
      && !entry.entity
      && PRIMITIVE_CONTEXT_TYPES.includes(entry.type);
  }

  static #normalizePrimitive(entry) {
    const normalized = {
      type: entry.type,
      entitySource: entry.entitySource,
      isPrimitive: true,
      isLegacyShape: false,
      mayBeEmpty: false,
    };
    if (entry.entitySource === KEY_URI) {
      normalized.uriVar = entry.uriVar;
    } else if (Object.prototype.hasOwnProperty.call(entry, "value")) {
      normalized.value = entry.value;
    }
    return normalized;
  }

  static #buildPrimitive(formState) {
    if (formState.entitySource === KEY_URI) {
      return { type: formState.type, entitySource: KEY_URI, uriVar: formState.uriVar };
    }
    if (formState.entitySource === KEY_CONSTANT) {
      return { type: formState.type, entitySource: KEY_CONSTANT, value: formState.value };
    }
    throw new Error(`ProvidesContextEntry.build: a ${formState.type} context comes from "${KEY_CONSTANT}" or "${KEY_URI}", `
      + `not "${formState.entitySource}".`);
  }

  static isValidContextName(name) {
    return typeof name === "string" && VALID_CONTEXT_NAME_PATTERN.test(name);
  }

  static sortByChainOrder(entriesMap) {
    const entries = entriesMap ?? {};
    const names = Object.keys(entries);
    const inMap = new Set(names);
    const ordered = [];
    const visited = new Set();
    const visiting = new Set();
    let cyclic = false;
    const visit = name => {
      if (visited.has(name)) {
        return;
      }
      if (visiting.has(name)) {
        cyclic = true;
        return;
      }
      visiting.add(name);
      const entry = entries[name];
      const source = entry?.entitySource === KEY_CONTEXT ? entry.sourceContext : null;
      if (source && inMap.has(source)) {
        visit(source);
      }
      visiting.delete(name);
      visited.add(name);
      ordered.push(name);
    };
    names.forEach(visit);
    if (cyclic) {
      // eslint-disable-next-line no-console
      console.error(
        "provides-contexts chain is CYCLIC — the resolver cannot order it; keeping declaration order",
        { entries: names },
      );
      return { ...entries };
    }
    return Object.fromEntries(ordered.map(name => [name, entries[name]]));
  }

  static isLegacyEntityType(entityType) {
    return typeof entityType === "string" && entityType.startsWith("Legacy");
  }

  static reservedNameError(name) {
    return isExpressionGlobalName(name) ? reservedGlobalNameMessage(name) : "";
  }

  static standardEntityError(entry) {
    if (entry?.entitySource !== KEY_STANDARD || VALID_STANDARD_ENTITIES.includes(entry.standardEntity)) {
      return null;
    }
    return `standard-source context names unknown standardEntity "${entry.standardEntity}" `
      + `— expected one of: ${VALID_STANDARD_ENTITIES.join(", ")}`;
  }
}

ProvidesContextEntry.KEY_URI = KEY_URI;
ProvidesContextEntry.KEY_CONSTANT = KEY_CONSTANT;
ProvidesContextEntry.KEY_CONFIG = KEY_CONFIG;
ProvidesContextEntry.KEY_CONTEXT = KEY_CONTEXT;
ProvidesContextEntry.KEY_STANDARD = KEY_STANDARD;
ProvidesContextEntry.KEY_STANDARD_ROOT = KEY_STANDARD_ROOT;
ProvidesContextEntry.KEY_MAY_BE_EMPTY = KEY_MAY_BE_EMPTY;
ProvidesContextEntry.KEY_STANDARD_RESUME_STORY = KEY_STANDARD_RESUME_STORY;
ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_PROFILE = KEY_STANDARD_CURRENT_USER_PROFILE;
ProvidesContextEntry.KEY_STANDARD_CURRENT_SUBSCRIPTION = KEY_STANDARD_CURRENT_SUBSCRIPTION;
ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_ACCESS = KEY_STANDARD_CURRENT_USER_ACCESS;
ProvidesContextEntry.KEY_STANDARD_CONTINUE_ACTIVITY = KEY_STANDARD_CONTINUE_ACTIVITY;
ProvidesContextEntry.KEY_STANDARD_CONTINUE_PROGRAM = KEY_STANDARD_CONTINUE_PROGRAM;
ProvidesContextEntry.VALID_STANDARD_ENTITIES = VALID_STANDARD_ENTITIES;
ProvidesContextEntry.KEY_SESSION = "session";
ProvidesContextEntry.KEY_CONTEXT_TYPE = "contextType";

module.exports = ProvidesContextEntry;
